"use client"

import { useCallback, useEffect, useRef } from "react"

/** Idle window after the last keystroke before we tell Chatwoot the customer
 *  stopped typing. Chatwoot's own indicator lingers a few seconds, so this sits
 *  just under that so the dashboard clears promptly without flapping. */
const IDLE_MS = 3_500

type TypingStatus = "on" | "off"

interface UseTypingEmitArgs {
  /** Active conversation id, from use-chat-session. */
  conversationId: number | null
  /** The per-contact widget JWT (session.source_id), sent as X-Auth-Token. */
  authToken: string | null
}

interface TypingEmitControls {
  /** Call on each keystroke in the composer. */
  onInput: () => void
  /** Call when the draft is sent (or cleared) so the indicator drops at once. */
  stop: () => void
}

/**
 * Emits the customer's typing status to Chatwoot so agents see a live "customer
 * is typing" indicator on the conversation.
 *
 * `typing_on` fires once on the first keystroke after an idle gap — the
 * `typingOnSent` guard means a burst of keystrokes sends one `on`, not one per
 * key — and `typing_off` fires after {@link IDLE_MS} of no typing or the moment
 * the message is sent. Fire-and-forget like useVisitorPresence: a dropped
 * status is cosmetic and self-heals on the next keystroke or send.
 *
 * Only emits when there is an active conversation and widget JWT, mirroring the
 * presence/read-receipt gating — a visitor Chatwoot has no contact for yet has
 * nothing to report typing against.
 */
export function useTypingEmit({
  conversationId,
  authToken,
}: UseTypingEmitArgs): TypingEmitControls {
  // Whether we've already told Chatwoot the customer is typing, so a burst of
  // keystrokes sends one typing_on rather than one per key.
  const typingOnSent = useRef(false)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const emit = useCallback(
    (status: TypingStatus): void => {
      if (!conversationId || !authToken) return
      void fetch("/api/chat/typing", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Token": authToken,
        },
        body: JSON.stringify({ typing_status: status }),
      }).catch(() => {
        // Best-effort and cosmetic: a dropped status just lets the agent's
        // indicator lapse, and the next keystroke or send restores it.
      })
    },
    [conversationId, authToken]
  )

  const clearIdleTimer = useCallback((): void => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current)
      idleTimer.current = null
    }
  }, [])

  const stop = useCallback((): void => {
    clearIdleTimer()
    if (!typingOnSent.current) return
    typingOnSent.current = false
    emit("off")
  }, [clearIdleTimer, emit])

  const onInput = useCallback((): void => {
    if (!conversationId || !authToken) return
    if (!typingOnSent.current) {
      typingOnSent.current = true
      emit("on")
    }
    clearIdleTimer()
    idleTimer.current = setTimeout(stop, IDLE_MS)
  }, [conversationId, authToken, emit, clearIdleTimer, stop])

  // Drop the indicator if the panel unmounts mid-compose.
  useEffect(() => stop, [stop])

  return { onInput, stop }
}
